import './UsersPage.scss';
import { Flex, Space, Typography } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from '../../i18n/i18n';
import NewUserModal from '../../components/users/NewUserModal';
import UsersTable from '../../components/users/UsersTable';
import { userApi } from '../../api/user.api';
import { User } from '../../gen/proto/api/gateway/v1/user_pb';
import useAuthStore from '../../stores/auth.store';
import useTenantStore from '../../stores/tenant.store';
import { ADAPTIVE_TABLE_CONTAINER_CLASS, useAdaptivePageSize } from '../../hooks/useAdaptivePageSize';

const FIRST_PAGE = 1;
const { Title } = Typography;
const UsersPage = () => {
  const { t } = useTranslation('users');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState<boolean>(true);

  const isSuperAdminOrCompanyAdmin = useAuthStore((state) => state.isSuperAdminOrCompanyAdmin);
  const selectedTenant = useTenantStore((state) => state.selectedTenant);
  const tenants = useTenantStore((state) => state.tenants);
  const { pageSize, containerRef, containerHeight, recalculate } = useAdaptivePageSize();

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const response = await userApi.listUsersByPage(page, pageSize);

        setUsers(
          response.users.map((u) => ({
            ...u,
            tenant: tenants.find((t) => t.value === u.tenantUuid)?.label ?? u.tenantUuid,
          })),
        );
        setTotalUsers(response.metadata?.totalCount ?? 0);
      } catch (error) {
        console.error("Users couldn't be fetched");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [page, pageSize, refreshKey, tenants]);

  useEffect(() => {
    setPage(FIRST_PAGE);
    setRefreshKey((prev) => !prev);
  }, [selectedTenant, pageSize]);

  const refreshTable = () => {
    setRefreshKey((prevState) => !prevState);
  };

  useLayoutEffect(() => {
    recalculate();
  }, [totalUsers, recalculate]);

  return (
    <Flex vertical>
      <br />
      <Space className={'UsersPage__header'} size={'middle'}>
        <div className={'UsersPage__header__title'}>
          <TeamOutlined className={'UsersPage__icon'} />
          <Title level={1} className={'UsersPage__title'}>
            {t('title')}
          </Title>
        </div>
        {isSuperAdminOrCompanyAdmin && <NewUserModal onUserUpdated={refreshTable} />}
      </Space>
      <br />
      <div
        className={`UsersPage__table ${ADAPTIVE_TABLE_CONTAINER_CLASS}`}
        style={{ height: containerHeight }}
        ref={containerRef}
      >
        <UsersTable
          items={users}
          loading={loading}
          onUserUpdated={refreshTable}
          pagination={{
            current: page,
            pageSize,
            total: totalUsers,
            showSizeChanger: false,
            hideOnSinglePage: true,
          }}
          onChange={(pagination) => setPage(pagination.current ?? 1)}
        />
      </div>
    </Flex>
  );
};

export default UsersPage;
